import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Paper
} from '@mui/material';
import baseURL from '../../config';
import Navbar from '../../Navbar/Navbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AssignedCrew = () => {
  const [crews, setCrews] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingCrews, setLoadingCrews] = useState(false);
  const [crewSearchText, setCrewSearchText] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [assignDate, setAssignDate] = useState('');

  // Fetch crewing agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${baseURL}/api/getcrewingAgentDetails`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgents(res.data || []);
      } catch (err) {
        setError('Failed to fetch agents');
      } finally {
        setLoadingAgents(false);
      }
    };

    fetchAgents();
  }, []);

  // Fetch crews by search input
  useEffect(() => {
    if (!crewSearchText || crewSearchText.length < 2) {
      setCrews([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setLoadingCrews(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${baseURL}/api/getAllCrewDetails?search=${crewSearchText}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCrews(res.data.data || []);
      } catch (err) {
        console.error('Failed to fetch crew');
      } finally {
        setLoadingCrews(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [crewSearchText]);

  // Assign crew
  const handleAssign = async () => {
    if (!selectedAgent) return toast.error('Please select a crewing agent.');
    if (selectedCrews.length === 0) return toast.error('Please select at least one crew.');
    if (!assignDate) return toast.error('Please select an assignment date.');

    try {
      const token = localStorage.getItem('token');

      const crewPayload = selectedCrews.map((crew) => ({
        crewId: crew._id,
        crewName: crew.name
      }));

      const payload = {
        crews: crewPayload,
        CrewingAgentId: selectedAgent._id,
        CrewingAgentName: selectedAgent.agentName,
        assignDate
      };

      // 🔁 Call API to get fullCrewDetails (but don't save assignment yet)
      const res = await axios.post(`${baseURL}/api/assigneCrew`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { fullCrewDetails } = res.data;

      // ✅ Check document presence
      const crewsWithoutDocuments = fullCrewDetails.filter((crew) => {
        const keys = Object.keys(crew);
        const docKeys = keys.filter((key) => key.toLowerCase().includes('document'));
        return !docKeys.some((key) => crew[key]?.filePath);
      });

      if (crewsWithoutDocuments.length > 0) {
        const names = crewsWithoutDocuments.map(c => c.name).join(', ');
        toast.error(`Assignment failed. The following crew do not have documents: ${names}`);
        return; // ❌ Stop — don't show "assigned successfully"
      }

      // ✅ Proceed only if all have documents
      toast.success('Crew(s) assigned successfully!');
      setSelectedCrews([]);
      setSelectedAgent(null);
      setAssignDate('');

    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign crew');
    }
  };



  return (
    <>
      <Navbar />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={5}
        bgcolor="#f5f5f5"
        sx={{ minHeight: 'calc(100vh - 104px)', overflow: 'hidden' }}
      >
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, minWidth: 400 }}>
          <Typography variant="h6" align="center" gutterBottom>
            🚢 Assign Crew
          </Typography>

          {loadingAgents ? (
            <Box textAlign="center"><CircularProgress /></Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} alignItems="center">

              {/* Crew Selector */}
              <Box sx={{ width: 400 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={crews}
                  getOptionLabel={(crew) => `${crew.name} (${crew.indosno})`}
                  filterSelectedOptions
                  value={selectedCrews}
                  onChange={(e, value) => setSelectedCrews(value)}
                  onInputChange={(e, value) => setCrewSearchText(value)}
                  loading={loadingCrews}
                  noOptionsText="No crew found, please type to search"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Crews *"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCrews ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Agent Selector */}
              <Box sx={{ width: 400 }}>
                <Autocomplete
                  options={agents}
                  getOptionLabel={(agent) => agent.agentName}
                  value={selectedAgent}
                  onChange={(e, value) => setSelectedAgent(value)}
                  renderInput={(params) => <TextField {...params} label="Select Crewing Agent *" />}
                />
              </Box>

              {/* Assignment Date */}
              <Box sx={{ width: 400 }}>
                <TextField
                  label="Assign Date *"
                  type="date"
                  value={assignDate}
                  onChange={e => setAssignDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>

              {/* Buttons */}
              <Box display="flex" justifyContent="center" gap={2} mt={1}>
                <Button variant="contained" color="success" onClick={handleAssign}>
                  ✅ Assign
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setSelectedCrews([]);
                    setSelectedAgent(null);
                    setAssignDate('');
                    setError('');
                    setMessage('');
                  }}
                >
                  ❌ Clear
                </Button>
              </Box>

              {/* Messages */}
              {message && <Alert severity="success">{message}</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          )}
        </Paper>
      </Box>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default AssignedCrew;